// ParentApprovalDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ParentApprovalDashboard.css';

const ParentApprovalDashboard = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [user, setUser] = useState(null);

  // Get token from localStorage
  const getToken = () => localStorage.getItem('nlngra_jwt');

  // Fetch pending approvals
  const fetchPendingApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch('https://nlngraclub.com/nlnapi/get_pending_approvals.php', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setPendingApprovals(data.pending_approvals || []);
        setApprovalHistory(data.history || []);
      } else {
        setMessage(data.message || 'Failed to load pending approvals');
        setIsError(true);
      }
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      setMessage('Network error. Please try again.');
      setIsError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get current user
  useEffect(() => {
    const userStr = localStorage.getItem('nlngra_user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
  }, []);

  useEffect(() => {
    if (user && user.membership_type === 'primary') {
      fetchPendingApprovals();
    }
  }, [user, fetchPendingApprovals]);

  const handleApprove = async (dependentId, token) => {
    setProcessingId(dependentId);
    setMessage('');
    setIsError(false);

    try {
      const response = await fetch('https://nlngraclub.com/nlnapi/parent_approve.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          token: token,
          action: 'approve',
          dependent_id: dependentId
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage(`${data.dependent_name || 'Dependent'} has been approved successfully!`);
        // Refresh the list
        await fetchPendingApprovals();
      } else {
        setMessage(data.message || 'Failed to approve dependent');
        setIsError(true);
      }
    } catch (error) {
      console.error('Error approving:', error);
      setMessage('Failed to process approval. Please try again.');
      setIsError(true);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (dependentId, token) => {
    setProcessingId(dependentId);
    setMessage('');
    setIsError(false);

    try {
      const response = await fetch('https://nlngraclub.com/nlnapi/parent_approve.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          token: token,
          action: 'decline',
          dependent_id: dependentId
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage(`${data.dependent_name || 'Dependent'} has been declined.`);
        await fetchPendingApprovals();
      } else {
        setMessage(data.message || 'Failed to decline dependent');
        setIsError(true);
      }
    } catch (error) {
      console.error('Error declining:', error);
      setMessage('Failed to process decline. Please try again.');
      setIsError(true);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysLeftText = (daysLeft) => {
    if (daysLeft === 0) return 'Expires today';
    if (daysLeft === 1) return 'Expires tomorrow';
    return `${daysLeft} days left`;
  };

  const getCategoryLabel = (categoryValue) => {
    const categories = {
      'junior_member': 'Junior Member',
      'family_member': 'Family Member'
    };
    return categories[categoryValue] || categoryValue?.replace('_', ' ') || 'Family Member';
  };

  if (loading) {
    return (
      <div className="parent-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading pending approvals...</p>
      </div>
    );
  }

  if (user && user.membership_type !== 'primary') {
    return (
      <div className="parent-dashboard-error">
        <div className="error-container">
          <i className="fas fa-lock"></i>
          <h3>Access Restricted</h3>
          <p>Only primary members can access the parent approval dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="parent-approval-dashboard"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="header-icon">
            <i className="fas fa-users"></i>
          </div>
          <div>
            <h2>Family Member Approvals</h2>
            <p>Review and manage pending family member registration requests</p>
          </div>
        </div>

        <AnimatePresence>
          {message && (
            <motion.div 
              className={`alert-message ${isError ? 'alert-error' : 'alert-success'}`}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <i className={`fas ${isError ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
              <span>{message}</span>
              <button className="alert-close" onClick={() => setMessage('')}>
                <i className="fas fa-times"></i>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tab Navigation */}
        <div className="dashboard-tabs">
          <button 
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <i className="fas fa-clock"></i>
            Pending Requests
            {pendingApprovals.length > 0 && (
              <span className="tab-badge">{pendingApprovals.length}</span>
            )}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <i className="fas fa-history"></i>
            History
          </button>
        </div>

        {/* Pending Requests Tab */}
        {activeTab === 'pending' && (
          <div className="pending-requests">
            {pendingApprovals.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-check-circle"></i>
                <h3>No Pending Requests</h3>
                <p>All family member requests have been processed.</p>
              </div>
            ) : (
              <div className="requests-list">
                {pendingApprovals.map((request) => (
                  <motion.div 
                    key={request.request_id}
                    className="request-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="request-header">
                      <div className="member-avatar">
                        {request.first_name?.charAt(0) || 'U'}
                      </div>
                      <div className="member-info">
                        <h3>{request.full_name}</h3>
                        <p className="member-email">
                          <i className="fas fa-envelope"></i> {request.email}
                        </p>
                        {request.phone && (
                          <p className="member-phone">
                            <i className="fas fa-phone"></i> {request.phone}
                          </p>
                        )}
                        <p className="member-number">
                          <i className="fas fa-id-card"></i> {request.membership_number}
                        </p>
                      </div>
                      <div className="expiry-badge" data-days={request.days_left <= 2 ? 'urgent' : 'normal'}>
                        <i className="fas fa-hourglass-half"></i>
                        {getDaysLeftText(request.days_left)}
                      </div>
                    </div>

                    <div className="request-details">
                      <div className="detail-row">
                        <span className="detail-label">Membership Category:</span>
                        <span className="detail-value">
                          <span className="category-badge">
                            {getCategoryLabel(request.membership_category)}
                          </span>
                        </span>
                      </div>
                      {request.date_of_birth && (
                        <div className="detail-row">
                          <span className="detail-label">Date of Birth:</span>
                          <span className="detail-value">{formatDate(request.date_of_birth)}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="detail-label">Requested On:</span>
                        <span className="detail-value">{formatDate(request.requested_at)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Expires:</span>
                        <span className="detail-value">{formatDate(request.token_expires)}</span>
                      </div>
                    </div>

                    <div className="request-actions">
                      <button 
                        className="action-btn approve"
                        onClick={() => handleApprove(request.dependent_id, request.approval_token)}
                        disabled={processingId === request.dependent_id}
                      >
                        {processingId === request.dependent_id ? (
                          <div className="btn-spinner"></div>
                        ) : (
                          <>
                            <i className="fas fa-check"></i>
                            Approve
                          </>
                        )}
                      </button>
                      <button 
                        className="action-btn decline"
                        onClick={() => handleDecline(request.dependent_id, request.approval_token)}
                        disabled={processingId === request.dependent_id}
                      >
                        {processingId === request.dependent_id ? (
                          <div className="btn-spinner"></div>
                        ) : (
                          <>
                            <i className="fas fa-times"></i>
                            Decline
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="approval-history">
            {approvalHistory.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-history"></i>
                <h3>No History</h3>
                <p>No previous approval requests found.</p>
              </div>
            ) : (
              <div className="history-list">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Requested</th>
                      <th>Resolved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvalHistory.map((item) => (
                      <tr key={item.request_id}>
                        <td className="history-name">
                          <strong>{item.full_name}</strong>
                          <br />
                          <small>{item.membership_number}</small>
                        </td>
                        <td>{item.email}</td>
                        <td>
                          <span className={`status-badge ${item.status}`}>
                            {item.status === 'approved' ? (
                              <><i className="fas fa-check"></i> Approved</>
                            ) : (
                              <><i className="fas fa-times"></i> Declined</>
                            )}
                          </span>
                        </td>
                        <td>{formatDate(item.requested_at)}</td>
                        <td>{formatDate(item.resolved_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="dashboard-footer">
          <button className="refresh-btn" onClick={fetchPendingApprovals} disabled={loading}>
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
            Refresh
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ParentApprovalDashboard;